import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  channelConfig,
  channelsForTargets,
  channelOverride,
  channelSourceUrl,
  withChannelOverride,
  withoutChannelOverride,
  parkedOverrides,
  channelPreviewNote,
} from '../../utils/socialChannelMedia.js';

// Instagram status the parent uses to gate Approve: 'idle' (no image / not targeted),
// 'loading', 'ready' or 'error'.
const fetchInstagramFraming = (url) =>
  fetch(`/company_posts/instagram_preview?url=${encodeURIComponent(url)}`, { headers: { Accept: 'application/json' } })
    .then((res) => res.json().then((data) => {
      if (!res.ok || !data.success || !data.url) throw new Error(data.error || 'preview failed');
      return data;
    }));

// Opens the Uploadcare dialog. With `sourceUrl` it opens that existing image straight into the
// crop step; without it, it asks for a new upload (still cropped to the channel's shapes).
const openUploadcareDialog = ({ sourceUrl, crop }) =>
  new Promise((resolve, reject) => {
    const uc = window.uploadcare;
    if (!uc || typeof uc.openDialog !== 'function') {
      reject(new Error('The image tool is still loading. Try again in a moment.'));
      return;
    }
    try {
      // 'url' (re-upload from the CDN link), not 'uploaded': it works for any image the post holds
      // (agent, link import or another Uploadcare project) and crops what the user actually sees,
      // including any edits already baked into the link.
      const file = sourceUrl ? uc.fileFrom('url', sourceUrl) : null;
      uc.openDialog(file, { imagesOnly: true, multiple: false, crop })
        .done((picked) => {
          if (!picked) { resolve(null); return; }
          picked
            .done((info) => resolve({ url: info.cdnUrl || (info.uuid ? `https://ucarecdn.com/${info.uuid}/` : null), mimeType: info.mimeType }))
            .fail(() => reject(new Error('Upload failed. Your previous image is still in place.')));
        })
        .fail(() => resolve(null)); // dialog closed
    } catch (e) {
      reject(e);
    }
  });

// One preview per channel the post goes to, each showing exactly what that channel will publish.
// Facebook shows its image as-is; Instagram shows the url the server will actually send
// (InstagramImageFraming), blurred bars and all. Images are shown whole at their real shape.
const ChannelImagePreviews = ({
  mainUrl,
  mainType,
  channelMedia,
  targets,
  editable,
  onChannelMediaChange,
  onInstagramStatusChange,
}) => {
  const channels = channelsForTargets(targets);
  const [activeKey, setActiveKey] = useState(channels[0]);
  const [igFraming, setIgFraming] = useState({ status: 'idle', url: null, adjustment: null });
  const [busy, setBusy] = useState(false);

  const isVideo = (mainType || '').includes('video');
  const igSource = channels.includes('instagram') && !isVideo ? channelSourceUrl(channelMedia, 'instagram', mainUrl) : null;

  useEffect(() => {
    if (!channels.includes(activeKey)) setActiveKey(channels[0]);
  }, [targets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!igSource) {
      setIgFraming({ status: 'idle', url: null, adjustment: null });
      return undefined;
    }
    let cancelled = false;
    setIgFraming({ status: 'loading', url: null, adjustment: null });
    fetchInstagramFraming(igSource)
      .then((data) => { if (!cancelled) setIgFraming({ status: 'ready', url: data.url, adjustment: data.adjustment }); })
      .catch(() => { if (!cancelled) setIgFraming({ status: 'error', url: null, adjustment: null }); });
    return () => { cancelled = true; };
  }, [igSource]);

  useEffect(() => {
    if (onInstagramStatusChange) onInstagramStatusChange(igFraming.status);
  }, [igFraming.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mainUrl) {
    return <div className="channel-preview-empty">No image yet</div>;
  }

  if (isVideo) {
    return (
      <div className="channel-preview">
        <video src={mainUrl} className="channel-preview-image" controls muted loop playsInline />
      </div>
    );
  }

  const config = channelConfig(activeKey);
  const override = channelOverride(channelMedia, activeKey);
  const sourceUrl = channelSourceUrl(channelMedia, activeKey, mainUrl);
  const parked = parkedOverrides(channelMedia, targets);

  const replaceChannelImage = async (fromUrl) => {
    setBusy(true);
    try {
      const result = await openUploadcareDialog({ sourceUrl: fromUrl, crop: config.cropPresets });
      if (result && result.url) {
        onChannelMediaChange(withChannelOverride(channelMedia, activeKey, result.url, result.mimeType || 'image/jpeg'));
      }
    } catch (e) {
      toast.error(e.message || 'Something went wrong with the image tool.');
    } finally {
      setBusy(false);
    }
  };

  let body;
  let note = null;
  if (config.framedByServer) {
    if (igFraming.status === 'loading' || igFraming.status === 'idle') {
      body = <div className="channel-preview-loading">Preparing the Instagram version…</div>;
    } else if (igFraming.status === 'error') {
      note = {
        tone: 'error',
        text: "We couldn't prepare the Instagram version. Try again, adjust the crop, or upload a different image.",
      };
    } else {
      body = <img src={igFraming.url} alt="Instagram preview" className="channel-preview-image" />;
      note = channelPreviewNote('instagram', igFraming.adjustment);
    }
  } else {
    body = <img src={sourceUrl} alt={`${config.label} preview`} className="channel-preview-image" />;
    note = channelPreviewNote(activeKey);
  }

  return (
    <div className="channel-preview">
      {channels.length > 1 ? (
        <div className="channel-preview-tabs" role="tablist">
          {channels.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === activeKey}
              className={`channel-preview-tab${key === activeKey ? ' active' : ''}`}
              onClick={() => setActiveKey(key)}
            >
              {channelConfig(key).label}
            </button>
          ))}
        </div>
      ) : (
        <div className="channel-preview-label">{config.label} preview</div>
      )}

      <div className="channel-preview-frame">{body}</div>

      {note && <small className={`channel-preview-note channel-preview-note--${note.tone}`}>{note.text}</small>}

      {override && (
        <small className="channel-preview-note channel-preview-note--info">
          {config.label} is using its own image. Changing the main image above won't change it.
        </small>
      )}

      {editable && (
        <div className="channel-preview-actions">
          <button type="button" className="channel-preview-btn" disabled={busy} onClick={() => replaceChannelImage(sourceUrl)}>
            Adjust crop
          </button>
          <button type="button" className="channel-preview-btn" disabled={busy} onClick={() => replaceChannelImage(null)}>
            Use a different image for {config.label}
          </button>
          {override && (
            <button
              type="button"
              className="channel-preview-btn"
              disabled={busy}
              onClick={() => onChannelMediaChange(withoutChannelOverride(channelMedia, activeKey))}
            >
              {channels.length > 1 ? 'Use the same image for both' : 'Use the main image'}
            </button>
          )}
        </div>
      )}

      {parked.map((c) => (
        <small key={c.key} className="channel-preview-note channel-preview-note--info">
          {/^[aeiou]/i.test(c.label) ? 'An' : 'A'} {c.label}-only image is saved and will be used if you post to {c.label}.
        </small>
      ))}
    </div>
  );
};

export default ChannelImagePreviews;
